import { useCallback, useRef, useState } from 'react';

import { useDraftLeadEmailMutation } from 'core/graphql';

import { newSmsRequestKey } from '../Sms/smsModel';
import { LeadEmailAiKind } from './aploraModel';
import { noteSalesAiError, resetSalesAiOff } from './salesAiOff';

/**
 * ТЗ 0120 — ИИ-письмо лиду (анкета дофина, договор, follow-up). Письмо
 * всегда по-польски, без подписи (подпись ставит форма); отправка — только
 * руками продавца из «Переписки» с `aiDraftId`.
 */

export interface ILeadEmailAiDraft {
  id: string;
  kind: LeadEmailAiKind;
  subject: string;
  html: string;
  text: string;
  cached: boolean;
  /** С чем черновик написан — «Сгенерировать заново» повторяет то же. */
  contractId: string | null;
  hint: string;
}

export interface ILeadEmailAiResult {
  draft: ILeadEmailAiDraft | null;
  /** Код отказа сервера или `NETWORK`. */
  errorCode: string | null;
  /** Текст сервера (бюджет — сколько осталось и т. п.). */
  errorMessage: string | null;
}

export interface ILeadEmailAiRequest {
  kind: LeadEmailAiKind;
  contractId?: string | null;
  hint?: string;
  /** «Сгенерировать заново» — мимо кеша, с новым ключом. */
  regenerate?: boolean;
}

/**
 * Один запрос за раз: клик, пока письмо пишется, отбивается без запроса.
 * `busy` — вид, который сейчас пишется.
 */
export const useLeadEmailDraft = (leadId: string) => {
  const [mutate] = useDraftLeadEmailMutation();
  const [busy, setBusy] = useState<LeadEmailAiKind | null>(null);
  const busyRef = useRef(false);

  const generate = useCallback(
    async ({
      kind,
      contractId = null,
      hint = '',
      regenerate = false,
    }: ILeadEmailAiRequest): Promise<ILeadEmailAiResult> => {
      // Двойной клик, пока первый запрос в пути, — не второй запрос.
      if (busyRef.current) {
        return { draft: null, errorCode: 'in_progress', errorMessage: null };
      }
      const cleanHint = hint.trim();
      // Ключ — на ОДИН запрос: новый клик — новый запрос (сервер сам
      // вернёт сохранённый черновик по отпечатку контекста).
      const requestKey = newSmsRequestKey();
      busyRef.current = true;
      setBusy(kind);
      try {
        const { data } = await mutate({
          variables: {
            leadId,
            kind,
            contractId: contractId || null,
            hint: cleanHint || null,
            regenerate,
            requestKey,
          },
        });
        const payload = data?.draftLeadEmail;
        const draft = payload?.draft;
        if (payload?.success && draft) {
          // ТЗ 0128: черновик есть — ИИ включён, запомненный отказ снят.
          resetSalesAiOff();
          return {
            draft: {
              id: draft.id,
              kind,
              subject: draft.subject,
              html: draft.html,
              text: draft.text,
              cached: Boolean(draft.cached),
              contractId: draft.contractId || contractId || null,
              hint: cleanHint,
            },
            errorCode: null,
            errorMessage: null,
          };
        }
        const error = payload?.customErrors?.find(Boolean);
        noteSalesAiError(error?.code);
        return {
          draft: null,
          errorCode: error?.code || 'internal_error',
          errorMessage: error?.message || null,
        };
      } catch (caught) {
        return { draft: null, errorCode: 'NETWORK', errorMessage: null };
      } finally {
        busyRef.current = false;
        setBusy(null);
      }
    },
    [leadId, mutate],
  );

  return { generate, busy };
};
